import { initializer, inject, injectable } from "@launchtray/tsyringe-async";
import { UpdateFirstTimeExperienceInputBody } from "by/gamefactory/cityversetycoon/networking/data/responses/firsttimeexperience/UpdateFirstTimeExperienceInputBody";
import { Bindable, NotifyProperty, nameof } from "../../../../shared/bindable-data";
import { INetworkClient } from "../../../networking/INetworkClient";
import { IFirstTimeExperienceModel } from "./IFirstTimeExperienceModel";
import { TooltipsInfo } from "./TooltipsInfo";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { IConnectionModel } from "../../../networking/IConnectionModel";

@injectable()
export class FirstTimeExperienceModel extends Bindable implements IFirstTimeExperienceModel {
    public readonly piggyIconInLobby: NotifyProperty<boolean>;
    public readonly tooltipsInfo: NotifyProperty<TooltipsInfo>;

    public readonly becameOwner: NotifyProperty<boolean>;
    public readonly becameRenter: NotifyProperty<boolean>;

    private readonly networkClient: INetworkClient;
    private readonly connectionModel: IConnectionModel;
    private readonly logger: ILogger;

    public constructor(
        @inject("INetworkClient") networkClient: INetworkClient,
        @inject("IConnectionModel") connectionModel: IConnectionModel,
        @inject('ILogger') logger: ILogger
    ) {
        super();
        this.networkClient = networkClient;
        this.connectionModel = connectionModel;
        this.logger = logger;
        this.piggyIconInLobby = new NotifyProperty<boolean>(this, nameof(this, "piggyIconInLobby"), false);
        this.tooltipsInfo = new NotifyProperty<TooltipsInfo>(this, nameof(this, "tooltipsInfo"), new TooltipsInfo());
        this.becameRenter = new NotifyProperty<boolean>(this, nameof(this, "becameRenter"), false);
        this.becameOwner = new NotifyProperty<boolean>(this, nameof(this, "becameOwner"), false);
        this.connectionModel.propertyChanged.add(this.onConnectionModelChanged, this);
    }

    public async getFirstTimeExperience(): Promise<void> {
        if (!this.connectionModel.connected.value) {
            return;
        }
        const firstTimeExperienceDto = await this.networkClient.getFirstTimeExperience();
        if (firstTimeExperienceDto == undefined) {
            throw new Error("Failed to load guest firstTimeExperienceDto.");
        }
        this.tooltipsInfo.value.updateValues(
            firstTimeExperienceDto.tooltips?.fixIntro,
            firstTimeExperienceDto.tooltips?.giftBox,
            firstTimeExperienceDto.tooltips?.hourlyBonusRollsIntro,
            firstTimeExperienceDto.tooltips?.hourlyBonusWheelIntro,
            firstTimeExperienceDto.tooltips?.myBuildingsCounter,
            firstTimeExperienceDto.tooltips?.raid,
            firstTimeExperienceDto.tooltips?.shopIntro,
            firstTimeExperienceDto.tooltips?.steal,
            firstTimeExperienceDto.tooltips?.visit,
            firstTimeExperienceDto.tooltips?.upgradeIntro,
            firstTimeExperienceDto.tooltips?.firstUpgradeByTokens,
            firstTimeExperienceDto.tooltips?.ownerWithoutBuildings,
            firstTimeExperienceDto.tooltips?.renterQuestIntro,
            firstTimeExperienceDto.tooltips?.tournamentsIntro,
            firstTimeExperienceDto.tooltips?.piggyIntro
        );
        this.becameRenter.setValue(firstTimeExperienceDto?.becameRenter);
        this.becameOwner.setValue(firstTimeExperienceDto?.becameOwner);
        this.piggyIconInLobby.setValue(!this.tooltipsInfo.value.upgradeIntro.value);
    }

    public async sendShownTooltip(input: Partial<UpdateFirstTimeExperienceInputBody>): Promise<void> {
        try {
            await this.networkClient.editFirstTimeExperience(input);
        } catch (e: unknown) {
            this.logger.error(e);
        }
    }

    private onConnectionModelChanged(model: IConnectionModel, key: string): void {
        switch (key) {
            case nameof(model, "connected"):
                if (!model.connected.value) {
                    this.piggyIconInLobby.setValue(false);
                    this.tooltipsInfo.value.reset();
                    this.becameOwner.setValue(false);
                    this.becameRenter.setValue(false);
                }
                break;
        }
    }
}
